import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import CauseCardContainer from '../CauseCardContainer/CauseCardContainer';
import EventCardContainer from '../EventCardContainer/EventCardContainer';
import { apiThunk } from '../../actions';
import UpdateTeamLocksModal from '../../components/UpdateTeamLocksModal/UpdateTeamLocksModal';
import {
  getLocks,
  getLocksConfigurableSelector,
  Locks,
} from '../../selectors/locksSelectors';
import { IAppState } from '../../types/state.d';
import TeamLockTypes from '../../consts/lockTypes';

type ManageLocksProps = {
  teamlocks: Locks;
  locksConfigurable: string[];
};

type DispatchFromProps = {
  updateTeam: typeof apiThunk.updateTeamLocks;
};
const mapDispatchToProps: DispatchFromProps = {
  updateTeam: apiThunk.updateTeamLocks,
};

type Props = ManageLocksProps & MappedDispatchProps<typeof mapDispatchToProps>;

export const ManageLocks = ({
  teamlocks: {
    charity: isCharityLocked,
    event: isEventLocked,
    campaign: isCampaignLocked,
  },
  locksConfigurable,
  updateTeam,
}: Props) => {
  const { t } = useTranslation();
  const isCharityLockConfigurable = locksConfigurable.includes('charity');
  const isEventLockConfigurable = locksConfigurable.includes('event');
  const isCampaignLockConfigurable = locksConfigurable.includes('campaign');
  const [modalLockType, setModalLockType] = useState<TeamLockTypes | null>(
    null,
  );

  const isError = false;
  return (
    <>
      <section className="jg-space-mbml" data-qa="manage-locks">
        <h2 className="jg-h4 jg-space-mbms">
          {t(['content.settings.cause.heading', 'Cause'])}
        </h2>

        {isCharityLocked ? (
          <>
            <CauseCardContainer />
            {isCharityLockConfigurable &&
              !isCampaignLocked && (
                <button
                  data-qa="cause-allow-button"
                  type="button"
                  className="jg-text--link jg-cursor-pointer jg-btn--transparent jg-space-mtsm"
                  onClick={() => setModalLockType(TeamLockTypes.Charity)}
                >
                  {t('content.settings.cause.allowButton')}
                </button>
              )}
          </>
        ) : (
          <p data-qa="cause-subtext">{t(['content.settings.cause.subtext'])}</p>
        )}
      </section>

      <section className="jg-space-mbml">
        <h2 className="jg-h4 jg-space-mbms">
          {t(['content.settings.event.heading', 'Event'])}
        </h2>
        {isEventLocked ? (
          <>
            <EventCardContainer />
            {isEventLockConfigurable &&
              !isCampaignLocked && (
                <button
                  data-qa="event-allow-button"
                  type="button"
                  className="jg-text--link jg-cursor-pointer jg-btn--transparent jg-space-mtsm"
                  onClick={() => setModalLockType(TeamLockTypes.Event)}
                >
                  {t('content.settings.event.allowButton')}
                </button>
              )}
          </>
        ) : (
          <p data-qa="event-subtext">{t(['content.settings.event.subtext'])}</p>
        )}
      </section>
      {isCampaignLockConfigurable &&
        isCampaignLocked && (
          <section className="jg-space-mbml">
            <button
              data-qa="campaign-allow-button"
              type="button"
              className="jg-text--link jg-cursor-pointer jg-btn--transparent"
              onClick={() => setModalLockType(TeamLockTypes.Campaign)}
            >
              {t('content.settings.campaign.allowButton')}
            </button>
          </section>
        )}
      {modalLockType !== null && (
        <UpdateTeamLocksModal
          teamLockType={modalLockType}
          updateTeamLocks={updateTeam}
          onCloseModal={() => setModalLockType(null)}
          isModalOpen={Boolean(modalLockType)}
          isInFlight={false}
          isError={isError}
        />
      )}
    </>
  );
};

export const mapStateToProps = (state: IAppState) => ({
  teamlocks: getLocks(state),
  locksConfigurable: getLocksConfigurableSelector(state),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ManageLocks as any);
